import { API_URL } from '../config/constants';
import { loadPlaidScript } from './plaid-link';

export const BANK_CONNECT_PROVIDER = Object.freeze({
  PLAID: 'plaid',
  MASTERCARD: 'mastercard',
  QUILTT: 'quiltt',
});

export const BANK_CONNECT_EXIT_REASON = Object.freeze({
  USER_CANCELLED: 'user_cancelled',
  NO_ACCOUNTS_SHARED: 'no_accounts_shared',
  DUPLICATE_ACCOUNTS: 'duplicate_accounts',
  PROVIDER_ERROR: 'provider_error',
  NETWORK_ERROR: 'network_error',
  UNKNOWN: 'unknown',
});

const normalizeProvider = (provider) => {
  const normalized = String(provider || BANK_CONNECT_PROVIDER.PLAID).trim().toLowerCase();
  if (normalized === BANK_CONNECT_PROVIDER.MASTERCARD) {
    return BANK_CONNECT_PROVIDER.MASTERCARD;
  }
  if (normalized === BANK_CONNECT_PROVIDER.QUILTT) {
    return BANK_CONNECT_PROVIDER.QUILTT;
  }
  return BANK_CONNECT_PROVIDER.PLAID;
};

const SUCCESS_CALLBACK_STATUSES = new Set(['complete', 'completed', 'success', 'succeeded']);
const CANCELLED_CALLBACK_STATUSES = new Set(['back', 'cancel', 'cancelled', 'canceled', 'close', 'closed', 'exit']);

export const buildBankConnectError = ({
  code = null,
  message = 'Bank connection failed',
  provider = BANK_CONNECT_PROVIDER.PLAID,
  reason = BANK_CONNECT_EXIT_REASON.UNKNOWN,
  responseData = null,
} = {}) => {
  const error = new Error(message);
  error.code = code;
  error.provider = normalizeProvider(provider);
  error.reason = reason;
  error.responseData = responseData;
  return error;
};

export const isUserCancelledBankConnectError = (error) => (
  error?.reason === BANK_CONNECT_EXIT_REASON.USER_CANCELLED
  || error?.code === 'USER_EXIT'
);

const normalizeRequestFailure = (error, fallbackMessage, provider) => {
  if (error?.reason) {
    return error;
  }

  const message = error?.message || fallbackMessage;
  const isNetworkError = error?.name === 'TypeError' || /failed to fetch/i.test(message);
  return buildBankConnectError({
    code: error?.code || (isNetworkError ? 'NETWORK_ERROR' : null),
    message,
    provider,
    reason: isNetworkError ? BANK_CONNECT_EXIT_REASON.NETWORK_ERROR : BANK_CONNECT_EXIT_REASON.UNKNOWN,
    responseData: error?.responseData || null,
  });
};

const normalizeCompletionFailure = (data, provider) => {
  const code = data?.code || null;
  let reason = BANK_CONNECT_EXIT_REASON.UNKNOWN;
  if (code === 'NO_ACCOUNTS_SHARED') {
    reason = BANK_CONNECT_EXIT_REASON.NO_ACCOUNTS_SHARED;
  } else if (code === 'DUPLICATE_ACCOUNTS' || code === 'DUPLICATE_ITEM') {
    reason = BANK_CONNECT_EXIT_REASON.DUPLICATE_ACCOUNTS;
  } else if (code) {
    reason = BANK_CONNECT_EXIT_REASON.PROVIDER_ERROR;
  }

  return buildBankConnectError({
    code,
    message: data?.error || 'Bank connection failed',
    provider,
    reason,
    responseData: data,
  });
};

const normalizePlaidExitError = (error) => {
  if (!error) {
    return buildBankConnectError({
      code: 'USER_EXIT',
      message: 'Bank connection was cancelled before completion.',
      provider: BANK_CONNECT_PROVIDER.PLAID,
      reason: BANK_CONNECT_EXIT_REASON.USER_CANCELLED,
    });
  }

  return buildBankConnectError({
    code: error.error_code || null,
    message: error.display_message || error.error_message || 'Bank connection failed',
    provider: BANK_CONNECT_PROVIDER.PLAID,
    reason: BANK_CONNECT_EXIT_REASON.PROVIDER_ERROR,
    responseData: error,
  });
};

const normalizeMastercardCallbackError = ({ query = {}, provider = BANK_CONNECT_PROVIDER.MASTERCARD } = {}) => {
  const normalizedQuery = query || {};
  const status = String(
    normalizedQuery.status
    || normalizedQuery.result
    || normalizedQuery.outcome
    || ''
  ).trim().toLowerCase();
  const errorCode = String(
    normalizedQuery.error_code
    || normalizedQuery.errorCode
    || ''
  ).trim() || null;
  const errorMessage = String(
    normalizedQuery.error
    || normalizedQuery.error_message
    || normalizedQuery.errorMessage
    || ''
  ).trim() || null;

  if (SUCCESS_CALLBACK_STATUSES.has(status)) {
    return null;
  }

  if (
    CANCELLED_CALLBACK_STATUSES.has(status)
    || normalizedQuery.cancelled === 'true'
    || normalizedQuery.canceled === 'true'
  ) {
    return buildBankConnectError({
      code: 'USER_EXIT',
      message: 'Bank connection was cancelled before completion.',
      provider,
      reason: BANK_CONNECT_EXIT_REASON.USER_CANCELLED,
      responseData: normalizedQuery,
    });
  }

  if (errorCode || errorMessage) {
    return buildBankConnectError({
      code: errorCode,
      message: errorMessage || 'Bank connection failed',
      provider,
      reason: BANK_CONNECT_EXIT_REASON.PROVIDER_ERROR,
      responseData: normalizedQuery,
    });
  }

  // Mastercard completion is verified server-side from the session id. Once the
  // provider returns to one of our callback pages, treat the callback as success
  // unless it explicitly signals cancellation or an error.
  if (Object.keys(normalizedQuery).length > 0) {
    return null;
  }

  return buildBankConnectError({
    code: errorCode,
    message: errorMessage || 'Bank connection was cancelled before completion.',
    provider,
    reason: errorCode || errorMessage
      ? BANK_CONNECT_EXIT_REASON.PROVIDER_ERROR
      : BANK_CONNECT_EXIT_REASON.USER_CANCELLED,
    responseData: normalizedQuery,
  });
};

const mastercardConnectEnabled = String(process.env.REACT_APP_MASTERCARD_CONNECT_ENABLED || '')
  .trim()
  .toLowerCase() === 'true';
const quilttConnectEnabled = String(process.env.REACT_APP_QUILTT_CONNECT_ENABLED || '')
  .trim()
  .toLowerCase() === 'true';
const QUILTT_CONNECTOR_SCRIPT_SRC = 'https://cdn.quiltt.io/v1/connector.js';
const BANK_CONNECT_COMPLETE_TIMEOUT_MS = 60_000;

export const MASTERCARD_CONNECT_CALLBACK_MESSAGE = 'vylo:mastercard-connect:callback';

export const getSearchConnectProvider = () => (
  quilttConnectEnabled ? BANK_CONNECT_PROVIDER.QUILTT : BANK_CONNECT_PROVIDER.PLAID
);

export const getInstitutionConnectProvider = (institution) => {
  if (quilttConnectEnabled) {
    return BANK_CONNECT_PROVIDER.QUILTT;
  }
  return normalizeProvider(institution?.provider);
};

export const getUpdateConnectProvider = (updateItemId) => {
  const raw = String(updateItemId || '').trim().toLowerCase();
  if (raw.startsWith(`${BANK_CONNECT_PROVIDER.MASTERCARD}:`)) {
    return BANK_CONNECT_PROVIDER.MASTERCARD;
  }
  if (raw.startsWith(`${BANK_CONNECT_PROVIDER.QUILTT}:`)) {
    return BANK_CONNECT_PROVIDER.QUILTT;
  }
  return BANK_CONNECT_PROVIDER.PLAID;
};

export const isBankConnectProviderEnabled = (provider) => {
  const normalized = normalizeProvider(provider);
  if (normalized === BANK_CONNECT_PROVIDER.MASTERCARD) {
    return mastercardConnectEnabled;
  }
  if (normalized === BANK_CONNECT_PROVIDER.QUILTT) {
    return quilttConnectEnabled;
  }
  return true;
};

export const getLaunchConnectProvider = ({
  provider = null,
  institution = null,
  fallbackProvider = BANK_CONNECT_PROVIDER.PLAID,
} = {}) => {
  if (quilttConnectEnabled) {
    return BANK_CONNECT_PROVIDER.QUILTT;
  }

  const requestedProvider = normalizeProvider(provider || institution?.provider || fallbackProvider);
  if (isBankConnectProviderEnabled(requestedProvider)) {
    return requestedProvider;
  }
  return normalizeProvider(fallbackProvider);
};

export const createBankConnectSession = async ({
  request,
  provider = BANK_CONNECT_PROVIDER.PLAID,
  updateItemId = null,
  migrationId = null,
  institution = null,
  householdId = null,
  userId = null,
  entry = 'featured',
}) => {
  const body = updateItemId
    ? { mode: 'update', item_id: updateItemId }
    : {};

  body.provider = normalizeProvider(provider);
  if (migrationId) body.migration_id = migrationId;
  if (householdId != null) body.householdId = householdId;
  if (userId != null) body.userId = userId;
  if (institution?.slug) body.institution_slug = institution.slug;
  if (institution?.name) body.institution_name = institution.name;
  if (institution?.searchName) body.institution_search = institution.searchName;
  if (entry) body.entry = entry;

  let response;
  try {
    response = await request(`${API_URL}/bank/connect/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (error) {
    throw normalizeRequestFailure(error, 'Could not start bank connection', provider);
  }

  const data = await response.json().catch(() => ({}));
  const responseProvider = normalizeProvider(data?.provider || provider);
  const hasLaunchPayload = Boolean(
    data?.link_token
    || data?.connect_url
    || (responseProvider === BANK_CONNECT_PROVIDER.QUILTT && data?.session_token && data?.connector_id)
  );

  if (!response.ok || !hasLaunchPayload) {
    throw normalizeCompletionFailure({
      ...data,
      error: data?.error || 'Could not start bank connection',
    }, provider);
  }

  return {
    provider: responseProvider,
    data,
  };
};

export const completeBankConnect = async ({
  request,
  provider = BANK_CONNECT_PROVIDER.PLAID,
  publicToken,
  sessionId = null,
  connectionId = null,
  connectorId = null,
  migrationId = null,
  callbackParams = null,
  householdId = null,
  userId = null,
  institution = null,
  timeoutMs = BANK_CONNECT_COMPLETE_TIMEOUT_MS,
}) => {
  const body = {
    provider: normalizeProvider(provider),
    public_token: publicToken,
    householdId,
    userId,
    institution_slug: institution?.slug || undefined,
    institution_name: institution?.name || undefined,
  };

  if (sessionId) body.session_id = sessionId;
  if (connectionId) body.connection_id = connectionId;
  if (connectorId) body.connector_id = connectorId;
  if (migrationId) body.migration_id = migrationId;
  if (callbackParams) body.callback_params = callbackParams;

  let response;
  let timeoutId = null;
  const controller = typeof AbortController !== 'undefined' && timeoutMs > 0
    ? new AbortController()
    : null;
  try {
    if (controller) {
      timeoutId = window.setTimeout(() => {
        controller.abort();
      }, timeoutMs);
    }
    response = await request(`${API_URL}/bank/connect/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      ...(controller ? { signal: controller.signal } : {}),
      body: JSON.stringify(body),
    });
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw buildBankConnectError({
        code: 'REQUEST_TIMEOUT',
        message: 'Bank connection is taking too long to confirm. Please try again.',
        provider,
        reason: BANK_CONNECT_EXIT_REASON.NETWORK_ERROR,
      });
    }
    throw normalizeRequestFailure(error, 'Bank connection failed', provider);
  } finally {
    if (timeoutId) {
      window.clearTimeout(timeoutId);
    }
  }

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw normalizeCompletionFailure(data, provider);
  }

  return data;
};

export const openPlaidConnect = async ({ token, onSuccess, onExit }) => {
  const loaded = await loadPlaidScript();
  if (!loaded || !window.Plaid) {
    throw new Error('Bank connection unavailable. Try refreshing.');
  }

  window.Plaid.create({
    token,
    onSuccess,
    onExit: (error, metadata) => {
      onExit?.(normalizePlaidExitError(error), metadata);
    },
  }).open();
};

const buildPopupFeatures = ({ width = 520, height = 760 } = {}) => {
  const dualScreenLeft = window.screenLeft ?? window.screenX ?? 0;
  const dualScreenTop = window.screenTop ?? window.screenY ?? 0;
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || window.screen.width;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || window.screen.height;
  const left = Math.max(0, dualScreenLeft + Math.round((viewportWidth - width) / 2));
  const top = Math.max(0, dualScreenTop + Math.round((viewportHeight - height) / 2));

  return [
    `width=${width}`,
    `height=${height}`,
    `left=${left}`,
    `top=${top}`,
    'scrollbars=yes',
    'resizable=yes',
    'status=no',
    'toolbar=no',
    'menubar=no',
  ].join(',');
};

export const openMastercardConnect = async ({
  url,
  sessionId,
  callbackOrigin = null,
  onSuccess,
  onExit,
}) => {
  if (typeof window === 'undefined') {
    throw new Error('Bank connection unavailable. Try again.');
  }

  const popup = window.open(url, 'vylo-mastercard-connect', buildPopupFeatures());
  if (!popup) {
    throw new Error('Please allow pop-ups to connect your bank.');
  }

  let completed = false;

  const cleanup = () => {
    window.removeEventListener('message', handleMessage);
    window.clearInterval(closePoll);
  };

  const handleMessage = (event) => {
    const allowedOrigins = new Set([
      String(callbackOrigin || '').trim(),
      String(window.location.origin || '').trim(),
    ].filter(Boolean));
    if (!allowedOrigins.has(String(event.origin || '').trim())) return;
    if (event.data?.type !== MASTERCARD_CONNECT_CALLBACK_MESSAGE) return;
    if (sessionId && event.data?.sessionId && event.data.sessionId !== sessionId) return;

    completed = true;
    cleanup();
    try {
      popup.close();
    } catch (_error) {
      // Ignore popup close errors.
    }

    const callbackParams = event.data?.query || {};
    const callbackError = normalizeMastercardCallbackError({
      query: callbackParams,
      provider: BANK_CONNECT_PROVIDER.MASTERCARD,
    });

    if (callbackError) {
      onExit?.(callbackError, {
        provider: BANK_CONNECT_PROVIDER.MASTERCARD,
        sessionId,
        callbackParams,
      });
      return;
    }

    onSuccess?.({
      provider: BANK_CONNECT_PROVIDER.MASTERCARD,
      sessionId,
      callbackParams,
    });
  };

  const closePoll = window.setInterval(() => {
    if (!popup.closed) return;
    cleanup();
    if (!completed) {
      onExit?.(
        buildBankConnectError({
          code: 'USER_EXIT',
          message: 'Bank connection was cancelled before completion.',
          provider: BANK_CONNECT_PROVIDER.MASTERCARD,
          reason: BANK_CONNECT_EXIT_REASON.USER_CANCELLED,
        }),
        {
          provider: BANK_CONNECT_PROVIDER.MASTERCARD,
          sessionId,
        }
      );
    }
  }, 400);

  window.addEventListener('message', handleMessage);
  popup.focus();
};

let quilttScriptPromise = null;

export const loadQuilttConnectorScript = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.resolve(false);
  }
  if (window.Quiltt) {
    return Promise.resolve(true);
  }
  if (quilttScriptPromise) {
    return quilttScriptPromise;
  }

  quilttScriptPromise = new Promise((resolve) => {
    const existing = document.querySelector(`script[src="${QUILTT_CONNECTOR_SCRIPT_SRC}"]`);
    const script = existing || document.createElement('script');

    if (existing?.dataset?.loaded === 'true') {
      resolve(Boolean(window.Quiltt));
      return;
    }

    const handleLoad = () => {
      script.dataset.loaded = 'true';
      resolve(Boolean(window.Quiltt));
    };
    const handleError = () => resolve(false);

    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });

    if (!existing) {
      script.src = QUILTT_CONNECTOR_SCRIPT_SRC;
      script.async = true;
      document.head.appendChild(script);
    }
  });

  return quilttScriptPromise;
};

const sanitizeQuilttConnectorMetadata = (metadata = null) => ({
  connectorId: metadata?.connectorId || metadata?.connector_id || null,
  connectionId: metadata?.connectionId || metadata?.connection_id || null,
  connectorSessionId: metadata?.connectorSession?.id || metadata?.connector_session_id || null,
});

const QUILTT_SUCCESS_EVENT_TYPES = new Set(['exited.successful', 'exited.success', 'success', 'successful']);
const QUILTT_ABORT_EVENT_TYPES = new Set(['exited.aborted', 'exited.abort', 'abort', 'aborted']);
const QUILTT_ERROR_EVENT_TYPES = new Set(['exited.errored', 'exited.error', 'error', 'errored']);

const normalizeQuilttEventType = (value) => String(value || '').trim().toLowerCase();

const normalizeQuilttEventArgs = (eventOrMetadata, metadata) => {
  if (typeof eventOrMetadata === 'string') {
    return {
      eventType: normalizeQuilttEventType(eventOrMetadata),
      metadata: metadata || null,
    };
  }

  const candidateMetadata = eventOrMetadata && typeof eventOrMetadata === 'object'
    ? eventOrMetadata
    : (metadata || null);
  return {
    eventType: normalizeQuilttEventType(
      candidateMetadata?.type
      || candidateMetadata?.eventType
      || metadata?.type
      || metadata?.eventType
    ),
    metadata: candidateMetadata,
  };
};

const normalizeQuilttExitError = ({ code, message, reason, metadata = null }) => (
  buildBankConnectError({
    code,
    message,
    provider: BANK_CONNECT_PROVIDER.QUILTT,
    reason,
    responseData: sanitizeQuilttConnectorMetadata(metadata),
  })
);

export const openQuilttConnect = async ({
  sessionToken,
  connectorId,
  institution = null,
  connectionId = null,
  onSuccess,
  onExit,
}) => {
  const loaded = await loadQuilttConnectorScript();
  if (!loaded || !window.Quiltt) {
    throw new Error('Bank connection unavailable. Try refreshing.');
  }
  if (!sessionToken || !connectorId) {
    throw new Error('Could not start bank connection.');
  }

  let completed = false;
  const safeExit = (error, metadata = null) => {
    if (completed) return;
    completed = true;
    onExit?.(error, sanitizeQuilttConnectorMetadata(metadata));
  };
  const safeSuccess = (metadata = null) => {
    if (completed) return;
    completed = true;
    const safeMetadata = sanitizeQuilttConnectorMetadata(metadata);
    onSuccess?.({
      provider: BANK_CONNECT_PROVIDER.QUILTT,
      connectionId: safeMetadata.connectionId,
      connectorId: safeMetadata.connectorId || connectorId,
      metadata: safeMetadata,
    });
  };
  const handleExitEvent = (eventOrMetadata, metadata = null) => {
    const normalized = normalizeQuilttEventArgs(eventOrMetadata, metadata);
    if (QUILTT_SUCCESS_EVENT_TYPES.has(normalized.eventType)) {
      safeSuccess(normalized.metadata);
      return;
    }
    if (QUILTT_ABORT_EVENT_TYPES.has(normalized.eventType)) {
      safeExit(
        normalizeQuilttExitError({
          code: 'USER_EXIT',
          message: 'Bank connection was cancelled before completion.',
          reason: BANK_CONNECT_EXIT_REASON.USER_CANCELLED,
          metadata: normalized.metadata,
        }),
        normalized.metadata
      );
      return;
    }
    if (QUILTT_ERROR_EVENT_TYPES.has(normalized.eventType)) {
      safeExit(
        normalizeQuilttExitError({
          code: 'QUILTT_CONNECT_ERROR',
          message: 'Bank connection failed',
          reason: BANK_CONNECT_EXIT_REASON.PROVIDER_ERROR,
          metadata: normalized.metadata,
        }),
        normalized.metadata
      );
    }
  };

  const connectorOptions = {
    onEvent: handleExitEvent,
    onExit: handleExitEvent,
    onExitSuccess: safeSuccess,
    onExitAbort: (metadata) => {
      safeExit(
        normalizeQuilttExitError({
          code: 'USER_EXIT',
          message: 'Bank connection was cancelled before completion.',
          reason: BANK_CONNECT_EXIT_REASON.USER_CANCELLED,
          metadata,
        }),
        metadata
      );
    },
    onExitError: (metadata) => {
      safeExit(
        normalizeQuilttExitError({
          code: 'QUILTT_CONNECT_ERROR',
          message: 'Bank connection failed',
          reason: BANK_CONNECT_EXIT_REASON.PROVIDER_ERROR,
          metadata,
        }),
        metadata
      );
    },
  };

  window.Quiltt.authenticate(sessionToken);
  const connector = connectionId
    ? (
        typeof window.Quiltt.reconnect === 'function'
          ? window.Quiltt.reconnect(connectorId, {
              ...connectorOptions,
              connectionId,
            })
          : null
      )
    : window.Quiltt.connect(connectorId, {
        ...connectorOptions,
        ...(institution ? { institution } : {}),
      });

  if (connectionId && !connector) {
    throw new Error('Bank reconnect unavailable. Try refreshing.');
  }

  if (!connector?.open) {
    throw new Error('Bank connection unavailable. Try refreshing.');
  }

  connector.open();
};

export const openBankConnect = async ({
  provider = BANK_CONNECT_PROVIDER.PLAID,
  data,
  onSuccess,
  onExit,
}) => {
  const normalizedProvider = normalizeProvider(provider);

  if (normalizedProvider === BANK_CONNECT_PROVIDER.QUILTT) {
    return openQuilttConnect({
      sessionToken: data?.session_token,
      connectorId: data?.connector_id,
      institution: data?.institution || null,
      connectionId: data?.repair_connection_id || data?.connection_id || null,
      onSuccess,
      onExit,
    });
  }

  if (normalizedProvider === BANK_CONNECT_PROVIDER.MASTERCARD || data?.connect_url) {
    return openMastercardConnect({
      url: data?.connect_url,
      sessionId: data?.session_id || null,
      callbackOrigin: data?.callback_origin || null,
      onSuccess,
      onExit,
    });
  }

  return openPlaidConnect({
    token: data?.link_token,
    onSuccess: (publicToken, metadata) => {
      onSuccess?.({
        provider: BANK_CONNECT_PROVIDER.PLAID,
        publicToken,
        metadata,
      });
    },
    onExit,
  });
};

export const extractConnectedAccountsPreview = ({
  metadata = null,
  accounts = null,
  institution = null,
  institutionName = null,
} = {}) => {
  const rawAccounts = Array.isArray(accounts)
    ? accounts
    : (Array.isArray(metadata?.accounts) ? metadata.accounts : []);
  const resolvedInstitutionName = String(
    institutionName
    || metadata?.institution?.name
    || institution?.name
    || ''
  ).trim();

  return rawAccounts
    .map((account, index) => {
      if (!account || typeof account !== 'object') return null;

      return {
        id: account.id || account.accountId || account.account_id || `preview-account-${index}`,
        accountId: account.accountId || account.account_id || account.id || null,
        name: account.name || account.official_name || account.officialName || account.display_name || 'Connected account',
        official_name: account.official_name || account.officialName || account.name || null,
        officialName: account.officialName || account.official_name || account.name || null,
        mask: account.mask || account.account_mask || '',
        subtype: account.subtype || account.subType || account.account_subtype || '',
        type: account.type || account.account_type || account.subtype || '',
        institutionName: account.institutionName || resolvedInstitutionName || null,
        currentBalance: account.currentBalance ?? account.current_balance ?? account.balance ?? undefined,
        availableBalance: account.availableBalance ?? account.available_balance ?? undefined,
        balance: account.balance ?? account.currentBalance ?? account.current_balance ?? undefined,
      };
    })
    .filter(Boolean);
};
